import React from 'react';
import { Card, Col, Row, Typography } from 'antd';
import type { PvpSyncCheck, PvpSyncTotals } from '@/types/pvp';
import { dataQualityLine, fmtNum } from './model';

const { Text } = Typography;

interface Props {
  totals: PvpSyncTotals;
  check: PvpSyncCheck;
}

/** The headline numbers of the range. Units are in the labels. */
export const SyncTiles: React.FC<Props> = ({ totals, check }) => {
  // No balls (every match refunded): the per-1,000 rates have nothing to divide, so no number.
  const rate = (value: number) => (totals.balls > 0 ? fmtNum(value) : '—');
  const tiles: Array<{ label: string; value: string }> = [
    { label: 'Matches', value: fmtNum(totals.matches, 0) },
    { label: 'Balls', value: fmtNum(totals.balls, 0) },
    { label: 'Phone desyncs per 1,000 balls', value: rate(totals.desyncPer1000) },
    { label: 'Phone input mismatches per 1,000 balls', value: rate(totals.inputsPer1000) },
    { label: 'Check p95 (ms)', value: fmtNum(check.p95) },
    { label: 'Stalls per match with a ball', value: fmtNum(totals.stallsPerMatch) },
    { label: 'Cuts per match with a ball', value: fmtNum(totals.cutsPerMatch) },
    { label: 'Resumes per match with a ball', value: fmtNum(totals.resumesPerMatch) },
    { label: 'Zero-ball matches', value: fmtNum(totals.zeroBallMatches, 0) },
  ];
  const { net } = totals;
  const quality = dataQualityLine(totals);
  return (
    <>
      <Row gutter={[12, 12]}>
        {tiles.map((tile) => (
          <Col key={tile.label} xs={12} sm={8} lg={6} xl={3}>
            <Card size="small" data-tile={tile.label}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {tile.label}
              </Text>
              <Text strong style={{ fontSize: 22 }}>
                {tile.value}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
        A ball can count for both phones, so the per-1,000 rates can pass 1,000. Aborts:{' '}
        {fmtNum(totals.abortsSim, 0)} sim, {fmtNum(totals.abortsLate, 0)} late. Failed checks:{' '}
        {fmtNum(totals.checkFailed, 0)} (stays 0 until the server logs failed checks).
      </Text>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
        Net class changes during matches: {fmtNum(net.good, 0)} to good, {fmtNum(net.fair, 0)} to
        fair, {fmtNum(net.poor, 0)} to poor, {fmtNum(net.unknown, 0)} to unknown. Link quality per
        ball is in the <Text code>quality</Text> row of the text table below.
      </Text>
      {quality && (
        <Text
          type="secondary"
          data-quality-line
          style={{ display: 'block', fontSize: 12, marginTop: 4 }}
        >
          {quality}
        </Text>
      )}
    </>
  );
};
